import { SUPPORTED_CHAIN_IDS } from '@ui/constants/chain';
import { getUniqueStaticTradePairs } from '@ui/constants/tokens/tradePairs';
import { useClientDataChainId } from '@ui/hooks/datachain';
import { useIsMounted } from '@ui/hooks/useIsMounted';
import { ChainId } from '@ui/types/chain';
import { TradePair } from '@ui/types/tradePair';
import { createBigNumberStorage } from '@ui/utils/createBigNumberStorage';
import { getLocalStorageKey } from '@ui/utils/getLocalStorageKey';
import { isDegenMode } from '@ui/utils/isDegenMode';
import { useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { useTradePairInfos } from '../../../hooks/trade/useTradePairInfos';
import { useLeverage } from './useLeverage';

export type TradingPairStore = {
  tradingPairMap: Record<ChainId, TradePair>;
  useTradingPair: () => TradePair; // non null because assume in defaultMap every chain has at least 1 trading pair
  setTradingPair: (token: TradePair, chainId: ChainId) => void;
};
const defaultMap = Object.fromEntries(
  SUPPORTED_CHAIN_IDS.map((id) => [id, getUniqueStaticTradePairs(id)[0]]),
) as Record<ChainId, TradePair>;

export const useTradingPair = create<TradingPairStore>()(
  persist(
    (set, get) => ({
      tradingPairMap: defaultMap,
      useTradingPair: () => {
        const dataChainId = useClientDataChainId();
        const isMounted = useIsMounted();

        const pairsConfig = useTradePairInfos();
        const leverage = useLeverage();

        const currentTradingPair =
          (isMounted
            ? get().tradingPairMap[dataChainId]
            : defaultMap[dataChainId]) || defaultMap[dataChainId];

        if (!currentTradingPair) return currentTradingPair;

        if (!pairsConfig) return currentTradingPair;

        const possibleResult = pairsConfig.find(
          (pair) =>
            pair.baseSymbol === currentTradingPair.baseSymbol &&
            isDegenMode(leverage) === pair.isDegenPair,
        );

        return possibleResult ?? currentTradingPair;
      },
      setTradingPair: (tradingPair, chainId) => {
        set({
          tradingPairMap: {
            ...get().tradingPairMap,
            [chainId]: tradingPair,
          },
        });
      },
    }),
    {
      version: 7, // pay attention once merged
      name: getLocalStorageKey('trade.trading-pair'),
      // saving config into localStorage and some of the value is Bignumber, when fetch back to use, it is string instead of Bignumber, need to serialize and deserialize it
      storage: createBigNumberStorage(),
    },
  ),
);

// check if current trade pair 's base pair is degen support
export const useIsBasePairSupportDegen = () => {
  const currentTradePairInfo = useTradingPair((s) => s.useTradingPair());
  const pairsConfigs = useTradePairInfos();

  return useMemo(
    () =>
      pairsConfigs?.findIndex(
        (p) =>
          p.baseSymbol === currentTradePairInfo?.baseSymbol && p.isDegenPair,
      ) !== -1,
    [pairsConfigs, currentTradePairInfo],
  );
};

export const useGetBaseTradePair = () => {
  const currentTradePairInfo = useTradingPair((s) => s.useTradingPair());
  const pairsConfigs = useTradePairInfos();

  return useMemo(
    () =>
      pairsConfigs?.find(
        (p) =>
          p.baseSymbol === currentTradePairInfo?.baseSymbol && !p.isDegenPair,
      ),
    [pairsConfigs, currentTradePairInfo],
  );
};
